import Phaser from 'phaser';
import  BattleStateManager  from './game/BattleStateManager';

export class GameManager {
  constructor(scene, battlefieldMargins = null, battlefieldSize = 'small') {
    this.scene = scene;
    
    // Battle tracking properties
    this.currentBattleId = null;
    this.battleConfig = null;

    // Use battlefield margins if provided, otherwise default to no margins
    // Battlefield is always 4096x4096 (hardcoded to match CameraController)
    const marginPercent = battlefieldMargins || { top: 0, bottom: 0, left: 0, right: 0 };
    const worldWidth = 4096;
    const worldHeight = 4096;
    const topMarginAdjustment = 0; // No special adjustment needed for square battlefield

    // Convert percentage margins to pixel margins
    const margin = {
      left: (marginPercent.left / 100) * worldWidth,
      right: (marginPercent.right / 100) * worldWidth,
      top: ((marginPercent.top / 100) * worldHeight)-topMarginAdjustment,
      bottom: ((marginPercent.bottom / 100) * worldHeight) +topMarginAdjustment
    };

    console.log('world margins:', margin);

    // Initialize worldBounds with margins applied
    const initialBounds = new Phaser.Geom.Rectangle(
      margin.left,
      margin.top,
      worldWidth - margin.left - margin.right,
      worldHeight - margin.top - margin.bottom
    );

    this.minSize = new Phaser.Geom.Point(100, 100);

    // Further tighten spawn area (heavier reduction up top to keep players lower)
    const shrinkLeft = initialBounds.width * 0.075;
    const shrinkRight = initialBounds.width * 0.075;
    const shrinkTop = initialBounds.height * 0.225; // Reduced from 0.30 to 0.15 to move spawn area higher
    const shrinkBottom = initialBounds.height * 0.225;
    const adjustedBounds = new Phaser.Geom.Rectangle(
      initialBounds.x + shrinkLeft,
      initialBounds.y + shrinkTop,
      Math.max(this.minSize.x, initialBounds.width - shrinkLeft - shrinkRight),
      Math.max(this.minSize.y, initialBounds.height - shrinkTop - shrinkBottom)
    );

    // worldBounds represents the damage/safe zone that shrinks over time
    // This is NOT the camera bounds - camera can view the full 4096x4096 battlefield
    // worldBounds starts as a smaller spawn area and shrinks to create a damage zone
    //this.worldBounds = adjustedBounds;
    this.spawnBoundsInitial = Phaser.Geom.Rectangle.Clone(adjustedBounds);

    // Zone bounds start as full battlefield square
    this.worldBounds = new Phaser.Geom.Rectangle(0, 0, 4096, 4096);
    //decrease the world bounds by 20% on each side
    this.worldBounds.width *= 0.9;
    this.worldBounds.height *= 0.7;
    this.worldBounds.x = (4096 - this.worldBounds.width) / 2;
    this.worldBounds.y = (4096 - this.worldBounds.height) / 2;

    // Expand zone bounds (worldBounds) to be larger than spawn area initially
    // This creates a buffer zone that shrinks down to the spawn area size
    /*const zoneExpansionX = adjustedBounds.width * 0.2; // Expand 20% on each side horizontally
    const zoneExpansionY = adjustedBounds.height * 0.2; // Expand 20% on each side vertically
    this.worldBounds = new Phaser.Geom.Rectangle(
      adjustedBounds.x - zoneExpansionX / 2,
      adjustedBounds.y - zoneExpansionY / 2,
      adjustedBounds.width + zoneExpansionX,
      adjustedBounds.height + zoneExpansionY
    );*/

    this.drawSpawnAreaOutline();
    this.minBoundaryAreaRatio = 0.15;
    this.minBoundaryArea = this.worldBounds.width * this.worldBounds.height * this.minBoundaryAreaRatio;
    this.reachedMinimumBoundary = false;

    // Debug spawn bounds calculation
    console.log('[GameManager] Spawn bounds calculation:', {
      worldWidth: worldWidth,
      worldHeight: worldHeight,
      margins: margin,
      initialBounds: {
        x: initialBounds.x,
        y: initialBounds.y,
        width: initialBounds.width,
        height: initialBounds.height,
        centerX: initialBounds.centerX,
        centerY: initialBounds.centerY
      },
      adjustedBounds: {
        x: adjustedBounds.x,
        y: adjustedBounds.y,
        width: adjustedBounds.width,
        height: adjustedBounds.height,
        centerX: adjustedBounds.centerX,
        centerY: adjustedBounds.centerY
      },
      shrinkAmounts: {
        left: shrinkLeft,
        right: shrinkRight,
        top: shrinkTop,
        bottom: shrinkBottom
      }
    });

    this.shrinkSpeed = 35; // pixels per second from each side, increased for the 5s window
    this.boundaryState = 'pausing'; // 'pausing', 'shrinking'
    this.stateTimer = 0;
    this.pauseDuration = 20000; // Initial pause: 20 seconds before first shrink
    this.shrinkDuration = 6000;
    this.shakeTriggered = false; // Prevent multiple camera shakes per cycle
    this.isFirstShrink = true; // Track if this is the first shrink cycle
    this.joinedHeroIds = new Set();
    this.manualPauseUntil = 0;
  }

  update(delta) {
    const now = this.scene?.time?.now || Date.now();
    if (this.manualPauseUntil && now < this.manualPauseUntil) {
      // Hold boundary in pausing state while manual focus/action is active
      this.boundaryState = 'pausing';
      this.stateTimer = 0;
      this.shakeTriggered = false;
      this.updateBoundaryVisuals(this.worldBounds);
      return;
    }

    if (this.manualPauseUntil && now >= this.manualPauseUntil) {
      this.manualPauseUntil = 0;
    }

    this.stateTimer += delta;
    if (this.boundaryState === 'pausing') {
      // Start camera shake 2 seconds before shrinking begins
      if (this.stateTimer >= this.pauseDuration - 2000 && !this.shakeTriggered) {
        // Make shake intensity relative to zoom level - less intense when zoomed in
        const baseIntensity = 0.0035;
        const currentZoom = this.scene.worldCamera.zoom;
        const zoomAdjustedIntensity = baseIntensity / (currentZoom * Math.sqrt(currentZoom)); // Much less intense when zoomed in
        this.scene.worldCamera.shake(1200, zoomAdjustedIntensity);
        this.shakeTriggered = true;
      }

      if (this.stateTimer >= this.pauseDuration) {
        this.boundaryState = 'shrinking';
        this.stateTimer = 0;
        this.shakeTriggered = false; // Reset for next cycle
      }
    } else if (this.boundaryState === 'shrinking') {
      const currentArea = this.worldBounds.width * this.worldBounds.height;
      if (currentArea <= this.minBoundaryArea) {
        this.reachedMinimumBoundary = true;
        this.boundaryState = 'pausing';
        this.stateTimer = 0;
        return;
      }
      if (this.stateTimer < this.shrinkDuration) {
        const shrinkAmount = this.shrinkSpeed * (delta / 1000);
        const projectedWidth = Math.max(this.minSize.x, this.worldBounds.width - shrinkAmount);
        const projectedHeight = Math.max(this.minSize.y, this.worldBounds.height - shrinkAmount);
        const projectedArea = projectedWidth * projectedHeight;
        if (projectedArea <= this.minBoundaryArea) {
          const aspect = this.worldBounds.width / this.worldBounds.height || 1;
          let targetHeight = Math.sqrt(this.minBoundaryArea / aspect);
          let targetWidth = this.minBoundaryArea / targetHeight;
          targetWidth = Math.max(this.minSize.x, targetWidth);
          targetHeight = Math.max(this.minSize.y, targetHeight);
          const deltaW = this.worldBounds.width - targetWidth;
          const deltaH = this.worldBounds.height - targetHeight;
          this.worldBounds.x += deltaW / 2;
          this.worldBounds.width = targetWidth;
          this.worldBounds.y += deltaH / 2;
          this.worldBounds.height = targetHeight;
          this.reachedMinimumBoundary = true;
          this.boundaryState = 'pausing';
          this.stateTimer = 0;
          return;
        }

        if (this.worldBounds.width > this.minSize.x) {
            this.worldBounds.x += shrinkAmount / 2;
            this.worldBounds.width -= shrinkAmount;
        }
        if (this.worldBounds.height > this.minSize.y) {
            this.worldBounds.y += shrinkAmount / 2;
            this.worldBounds.height -= shrinkAmount;
        }
      } else {
        this.boundaryState = 'pausing';
        this.stateTimer = 0;
        // After first shrink, use normal pause duration (10 seconds)
        if (this.isFirstShrink) {
          this.isFirstShrink = false;
          this.pauseDuration = 10000;
        }
      }
    }

    this.updateBoundaryVisuals(this.worldBounds);
  }

  pauseBoundaryShrinkFor(durationMs = 0) {
    const now = this.scene?.time?.now || Date.now();
    const duration = Math.max(0, durationMs || 0);
    this.manualPauseUntil = Math.max(this.manualPauseUntil || 0, now + duration);
    this.boundaryState = 'pausing';
    this.stateTimer = 0;
    this.shakeTriggered = false;
  }

  isInsideBoundary(x, y) {
    return this.worldBounds.contains(x, y);
  }

  updateBoundaryVisuals(bounds) {
    // Draw orange zone bounds rectangle outline (shrinking damage zone)
    if (this.scene.boundaryGraphics) {
      this.scene.boundaryGraphics.clear();
      this.scene.boundaryGraphics.lineStyle(1, 0xffa500, 0.1); // Orange, thickness 10
      this.scene.boundaryGraphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    }
  }

  drawSpawnAreaOutline() {
    if (!this.scene) return;
    const bounds = this.spawnBoundsInitial || this.worldBounds;
    if (!bounds) return;

    if (!this.spawnAreaGraphics || !this.spawnAreaGraphics.scene) {
      this.spawnAreaGraphics = this.scene.add.graphics();
      this.spawnAreaGraphics.setDepth(3);
    }

    this.spawnAreaGraphics.clear();
    this.spawnAreaGraphics.lineStyle(1, 0xff0000, 0.1);
    this.spawnAreaGraphics.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    
    // Draw a visual marker at the calculated center for debugging
    /*const centerX = bounds.centerX;
    const centerY = bounds.centerY;
    this.spawnAreaGraphics.lineStyle(5, 0x00ff00, 1.0); // Green crosshair at center
    const crosshairSize = 50;
    this.spawnAreaGraphics.lineBetween(centerX - crosshairSize, centerY, centerX + crosshairSize, centerY);
    this.spawnAreaGraphics.lineBetween(centerX, centerY - crosshairSize, centerX, centerY + crosshairSize);
    
    console.log('[GameManager] drawSpawnAreaOutline - red rectangle and green crosshair:', {
      rectangle: { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height },
      center: { x: centerX, y: centerY },
      expectedFocusMarker: { x: centerX, y: centerY }
    });*/

    //ignore the spawn area graphics on the ui camera
    if (this.scene.uiCamera) {
      this.scene.uiCamera.ignore(this.spawnAreaGraphics);
    }
  }

  // Battle tracking methods
  async createBattle(themeId, mode, maxPlayers, config, battlefieldId, sessionId, battlefieldInstanceId = null) {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for battle creation');
        return null;
      }

      console.log('[GameManager] creating battle with theme:', themeId, 'mode:', mode, 'maxPlayers:', maxPlayers, 'config:', config, 'sessionId:', sessionId, 'battlefieldInstanceId:', battlefieldInstanceId);

      const response = await fetch('/api/battles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          themeId,
          mode,
          maxPlayers,
          config,
          battlefieldId,
          battlefieldInstanceId,
          sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          this.currentBattleId = data.data.id;
          this.battleConfig = config;
          console.log('✅ Battle created:', this.currentBattleId);
          return this.currentBattleId;
        }
      }
      
      console.error('Failed to create battle:', response.statusText);
      return null;
    } catch (error) {
      console.error('Error creating battle:', error);
      return null;
    }
  }

  async joinBattle(heroId, team = null) {
    if (!this.currentBattleId) {
      console.warn('No active battle to join');
      return false;
    }

    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for battle join');
        return false;
      }

      console.log('[GameManager] joining battle:', this.currentBattleId, 'hero:', heroId, 'team:', team);

      const response = await fetch(`/api/battles/${this.currentBattleId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          heroId,
          team
        })
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error('Failed to join battle:', response.status, response.statusText, text);
        return false;
      }

      const json = await response.json().catch(() => null);
      const inventory =
        (json && json.data && Array.isArray(json.data.inventory) && json.data.inventory) ||
        (json && Array.isArray(json.inventory) && json.inventory) ||
        [];
      if (Array.isArray(inventory)) {
        BattleStateManager.setHeroInventory(heroId, inventory);
      }
      const takeover = json?.data?.takeover;
      const alreadyParticipating = json?.data?.alreadyParticipating;
      console.log('✅ Hero joined battle:', { heroId, takeover, alreadyParticipating });
      return true;
    } catch (error) {
      console.error('Error joining battle:', { error, battleId: this.currentBattleId, heroId, team });
      return false;
    }
  }

  async joinBattleBatch(heroes) {

    if (!this.currentBattleId) {
      console.warn('No active battle to join');
      return false;
    }

    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for battle join');
        return false;
      }

      console.log('[GameManager] batch join battle:', this.currentBattleId, 'heroes:', heroes);

      const response = await fetch(`/api/battles/${this.currentBattleId}/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          heroes
        })
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error('Failed to join battle:', response.status, response.statusText, text);
        return false;
      }

      const json = await response.json().catch(() => null);
      const resultArray = Array.isArray(json?.data) ? json.data : [];
      if (Array.isArray(resultArray)) {
        resultArray.forEach((entry) => {
          if (!entry) return;
          const heroId = entry.heroId || entry.hero_id;
          if (!heroId) return;
          const snapshot =
            (Array.isArray(entry.inventory) && entry.inventory) ||
            (entry.data && Array.isArray(entry.data.inventory) && entry.data.inventory) ||
            [];
          BattleStateManager.setHeroInventory(heroId, snapshot);
        });
      }
      console.log('✅ Heroes joined battle:', heroes);
      return true;
    } catch (error) {
      console.error('Error joining battle:', { error, battleId: this.currentBattleId, heroes});
      return false;
    }
  }

  async ensureHeroJoined(heroId, team = null) {
    if (!this.currentBattleId || !heroId) return false;
    if (this.joinedHeroIds.has(heroId)) return true;
    const ok = await this.joinBattle(heroId, team);
    if (ok) this.joinedHeroIds.add(heroId);
    return ok;
  }

  async recordHeroStats(heroId, stats) {
    if (!this.currentBattleId) {
      console.warn('[GameManager] No active battle to record stats for');
      return false;
    }

    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for recording stats');
        return false;
      }

      const response = await fetch(`/api/battles/${this.currentBattleId}/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          heroId,
          ...stats
        })
      });

      if (response.ok) {
        const data = await response.json();
        const xp = typeof data?.xpEarned === 'number' ? data.xpEarned : null;
        console.log('✅ Hero stats recorded:', { battleId: this.currentBattleId, heroId, stats, xpEarned: xp, response: data });
        return xp;
      }

      const text = await response.text().catch(() => '');
      console.error('Failed to record hero stats:', { status: response.status, statusText: response.statusText, body: text });
      return null;
    } catch (error) {
      console.error('Error recording hero stats:', { error, battleId: this.currentBattleId, heroId, stats });
      return null;
    }
  }

  async recordBatchHeroStats(playerRankings, battlefieldRemainingLoot = [], battlefieldInstanceId = null, inventoryState = []) {
    if (!this.currentBattleId) {
      console.warn('[GameManager] No active battle to record stats for');
      return false;
    }
    
    
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for recording batch stats');
        return false;
      }

      const requestBody = {
        playerRankings
      };

      // Add battlefield loot data if provided
      if (battlefieldRemainingLoot && battlefieldRemainingLoot.length > 0) {
        requestBody.battlefieldRemainingLoot = battlefieldRemainingLoot;
      }
      if (battlefieldInstanceId) {
        requestBody.battlefieldInstanceId = battlefieldInstanceId;
      }
      if (Array.isArray(inventoryState) && inventoryState.length > 0) {
        requestBody.inventoryState = inventoryState;
      }

      const response = await fetch(`/api/battles/${this.currentBattleId}/record-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);
        console.log('✅ Batch stats recorded:', { playerRankings, battlefieldRemainingLoot, battlefieldInstanceId, inventoryState, response: data });
        return data?.result || null;
      }

      console.error('Failed to record batch stats:', response.status, response.statusText);
      return null;
    } catch (error) {
      console.error('Error recording batch stats:', { error, battleId: this.currentBattleId, playerRankings });
      return null;
    } 
  }

  async finishBattle(status = 'completed') {
    if (!this.currentBattleId) {
      console.warn('No active battle to finish');
      return false;
    }

    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for finishing battle');
        return false;
      }

      const response = await fetch(`/api/battles/${this.currentBattleId}/finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          status,
          endedAt: new Date().toISOString()
        })
      });

      if (response.ok) {
        console.log('✅ Battle finished:', this.currentBattleId, status);
        return true;
      }

      console.error('Failed to finish battle:', response.statusText);
      return false;
    } catch (error) {
      console.error('Error finishing battle:', error);
      return false;
    }
  }

  buildInventoryState(playerRankings) {
    if (!Array.isArray(playerRankings)) {
      console.warn('[GameManager.buildInventoryState] playerRankings is not an array:', playerRankings);
      return [];
    }

    console.log(`[GameManager.buildInventoryState] 🔍 Analyzing ${playerRankings.length} player rankings:`, {
      totalRankings: playerRankings.length,
      rankingsStructure: playerRankings.map((ranking, idx) => ({
        index: idx,
        hasPlayer: !!ranking?.player,
        playerName: ranking?.player?.name || 'N/A',
        heroId: ranking?.player?.heroId || ranking?.heroId || 'N/A',
        hasHeroId: !!(ranking?.player?.heroId || ranking?.heroId),
        rankingKeys: Object.keys(ranking || {}),
        playerKeys: ranking?.player ? Object.keys(ranking.player) : []
      }))
    });

    const inventoryState = playerRankings
      .filter((ranking) => {
        const heroId = ranking?.player?.heroId || ranking?.heroId;
        const hasHeroId = !!heroId;
        if (!hasHeroId) {
          console.log(`[GameManager.buildInventoryState] ⚠️ Filtering out ranking (no heroId):`, {
            playerName: ranking?.player?.name || 'N/A',
            hasPlayer: !!ranking?.player,
            heroId: heroId,
            rankingStructure: Object.keys(ranking || {})
          });
        }
        return hasHeroId;
      })
      .map((ranking) => {
        const player = ranking.player;
        const heroId = player?.heroId || ranking?.heroId;
        
        if (!heroId) {
          console.warn(`[GameManager.buildInventoryState] ⚠️ No heroId found for player:`, {
            playerName: player?.name || 'N/A',
            ranking: ranking
          });
          return null;
        }
        let inventory = [];
        let consumption = [];
        if (typeof player.buildInventorySyncState === 'function') {
          const snapshot = player.buildInventorySyncState();
          if (snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)) {
            inventory = Array.isArray(snapshot.slots) ? snapshot.slots : [];
            consumption = Array.isArray(snapshot.consumption) ? snapshot.consumption : [];
          } else if (Array.isArray(snapshot)) {
            inventory = snapshot;
          }
        } else if (Array.isArray(player.inventory)) {
          inventory = player.inventory
            .filter((entry) => entry && (entry.qty ?? entry.quantity ?? 0) > 0)
            .map((entry) => ({
              itemId: entry.itemId || entry.themeItemId || entry.id,
              quantity: Number(entry.qty ?? entry.quantity ?? 0) || 0,
              itemType: entry.itemType || 'consumable',
            }));
        }
        
        // Log inventory state being sent to server
        const totalOriginalConsumed = consumption.reduce((sum, c) => sum + (c.consumedOriginal || 0), 0);
        const totalLootConsumed = consumption.reduce((sum, c) => sum + (c.consumedLoot || 0), 0);
        console.log(`[GameManager.buildInventoryState] 📦 Building inventory state for hero ${heroId}:`, {
          heroId,
          playerName: player.name,
          inventorySlots: inventory.length,
          consumptionEntries: consumption.length,
          totalOriginalConsumed,
          totalLootConsumed,
          inventoryItems: inventory.map(i => ({ itemId: i.itemId, quantity: i.quantity })),
          consumptionDetails: consumption
        });
        
        return {
          heroId,
          inventory,
          consumption,
        };
      })
      .filter(Boolean); // Remove any null entries
    
    console.log(`[GameManager.buildInventoryState] ✅ Built inventory state for ${inventoryState.length} heroes`, {
      totalRankings: playerRankings.length,
      heroesWithInventory: inventoryState.length,
      inventoryStateDetails: inventoryState.map(state => ({
        heroId: state.heroId,
        inventoryCount: state.inventory?.length || 0,
        consumptionCount: state.consumption?.length || 0
      }))
    });
    return inventoryState;
  }

  // Convenience: record all hero results, then finish the battle
  // playerRankings: serverPlayerRankings (minimal, for stats recording)
  // originalPlayerRankings: optional, original playerRankings with player objects (for inventory building)
  async recordAndFinishBattle(playerRankings, battleDurationSeconds, totalPlayers, status = 'completed', battlefieldRemainingLoot = [], battlefieldInstanceId = null, originalPlayerRankings = null) {
    const activeBattleId = this.currentBattleId;
    if (!activeBattleId) {
      console.warn('No active battle to record/finish');
    }

    try {

      console.log('[GameManager recordAndFinishBattle] playerRankings:', playerRankings);
      console.log('[GameManager recordAndFinishBattle] battlefieldRemainingLoot:', battlefieldRemainingLoot);
      console.log('[GameManager recordAndFinishBattle] battlefieldInstanceId:', battlefieldInstanceId);
      
      // Record hero stats for each player that has a heroId
      /*for (const ranking of (playerRankings || [])) {
        const heroId = ranking?.player?.heroId;
        if (!heroId) continue;
        const stats = {
          finalPosition: ranking.finalPosition,
          eliminations: ranking.eliminations,
          damageDealt: ranking.player?.damageDealt || 0,
          damageTaken: ranking.player?.damageTaken || 0
        };
        const ok = await this.recordHeroStats(heroId, stats);
        if (!ok) console.error('Failed to record hero stats for hero:', heroId);
      }*/

      console.log('[GameManager recordAndFinishBattle] playerRankings:', playerRankings);
      
      // Use originalPlayerRankings (with player objects) for inventory building if available
      // Otherwise fall back to playerRankings (which may not have player objects)
      const rankingsForInventory = originalPlayerRankings || playerRankings;
      console.log('[GameManager recordAndFinishBattle] Using rankings for inventory:', {
        hasOriginalRankings: !!originalPlayerRankings,
        originalCount: originalPlayerRankings?.length || 0,
        serverCount: playerRankings?.length || 0,
        usingCount: rankingsForInventory?.length || 0
      });
      
      const inventoryState = this.buildInventoryState(rankingsForInventory);
      
      // Log what we're sending to server
      console.log(`[GameManager.recordAndFinishBattle] 📤 Sending to server - ORDER MATTERS:`, {
        step: 'BEFORE sending to server',
        note: 'Inventory consumption should be processed BEFORE new loot is added',
        inventoryStateCount: inventoryState.length,
        inventoryStateSummary: inventoryState.map(state => ({
          heroId: state.heroId,
          inventoryItems: state.inventory?.length || 0,
          consumptionItems: state.consumption?.length || 0,
          totalOriginalConsumed: state.consumption?.reduce((sum, c) => sum + (c.consumedOriginal || 0), 0) || 0,
          totalLootConsumed: state.consumption?.reduce((sum, c) => sum + (c.consumedLoot || 0), 0) || 0
        }))
      });
      
      const batchResult = await this.recordBatchHeroStats(
        playerRankings,
        battlefieldRemainingLoot,
        battlefieldInstanceId,
        inventoryState
      );

      // Finish the battle
      if (activeBattleId) {
        if (!this.currentBattleId) {
          this.currentBattleId = activeBattleId;
        }
        const finished = await this.finishBattle(status);
        if (!finished) {
          console.warn('[GameManager recordAndFinishBattle] Battle finish call failed');
        } else {
          this.currentBattleId = null;
        }
      }
      return batchResult;
    } catch (error) {
      console.error('Error recording and finishing battle:', error);
      return false;
    }
  }

  // Legacy battle recording methods for backwards compatibility
  async recordBattleStats(walletAddress, finalPosition, eliminations, battleDurationSeconds, totalPlayers) {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for recording battle stats');
        return false;
      }

      console.log('[GameManager] Recording battle stats for wallet:', walletAddress?.substring(0, 8) + '...');

      const response = await fetch('/api/battles/record', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          solana_wallet_address: walletAddress,
          final_position: finalPosition,
          eliminations_count: eliminations,
          battle_duration_seconds: battleDurationSeconds,
          total_players: totalPlayers
        })
      });

      if (response.ok) {
        console.log('✅ Battle stats recorded for wallet:', walletAddress?.substring(0, 8) + '...');
        return true;
      }

      console.error('Failed to record battle stats:', response.status, response.statusText);
      return false;
    } catch (error) {
      console.error('Error recording battle stats:', error);
      return false;
    }
  }

  async recordHeroBattleStats(heroId, finalPosition, eliminations, damageDealt, damageTaken, battleDurationSeconds, totalPlayers, isTokenHolder = false) {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        console.warn('No JWT token available for recording hero battle stats');
        return false;
      }

      console.log('[GameManager] Recording hero battle stats for hero:', heroId);

      const response = await fetch('/api/battles/record-hero', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          hero_id: heroId,
          final_position: finalPosition,
          eliminations_count: eliminations,
          damage_dealt: damageDealt,
          damage_taken: damageTaken,
          battle_duration_seconds: battleDurationSeconds,
          total_players: totalPlayers,
          is_token_holder: isTokenHolder
        })
      });

      if (response.ok) {
        console.log('✅ Hero battle stats recorded for hero:', heroId);
        return true;
      }

      console.error('Failed to record hero battle stats:', response.status, response.statusText);
      return false;
    } catch (error) {
      console.error('Error recording hero battle stats:', error);
      return false;
    }
  }

  // Batch method to record all battle stats for a completed battle
  async recordAllBattleStats(playerRankings, battleDurationSeconds, totalPlayers) {
    console.log('[GameManager] Recording all battle stats for', playerRankings.length, 'players');
    console.log('NOTUSED: recordAllBattleStats');
    return;
  }
}
